import { useQuery } from '@tanstack/react-query';
import axios from '../../../../services/axiosInstance';
import { ReviewsData, Review, ReviewStatus, StarRating } from '../types/Review';

const convertRating = (star: string): StarRating => {
  switch (star) {
    case 'ONE': return StarRating.ONE;
    case 'TWO': return StarRating.TWO;
    case 'THREE': return StarRating.THREE;
    case 'FOUR': return StarRating.FOUR;
    case 'FIVE': return StarRating.FIVE;
    default: return 0 as StarRating;
  }
};

interface FetchParams {
  locationName: string;
  accountName?: string;
  subjectEmail?: string;
  max?: number;
}

const fetchReviews = async ({ locationName, accountName, subjectEmail, max = 50 }: FetchParams): Promise<ReviewsData> => {
  if (!locationName) throw new Error('locationName est requis');

  const res = await axios.get('/api/gbp/performance/location-reviews', {
    params: { locationName, accountName, subjectEmail, max }
  });

  const data = res.data;

  const mappedReviews: Review[] = (data.reviews || []).map((r: any) => ({
    id: r.reviewId,
    userName: r.reviewer?.displayName || 'Anonyme',
    userInitial: (r.reviewer?.displayName || 'A')[0].toUpperCase(),
    message: r.comment || '',
    rating: convertRating(r.starRating),
    date: new Date(r.createTime),
    status: r.reviewReply ? ReviewStatus.REPLIED : ReviewStatus.NOT_REPLIED,
    hasMore: false,
    raw: r,
  }));

  return {
    totalReviews: data.totalCount ?? mappedReviews.length,
    averageRating: data.averageRating ?? mappedReviews.reduce((sum, r) => sum + r.rating, 0) / (mappedReviews.length || 1),
    ratingDistribution: {},
    chartData: [],
    reviews: mappedReviews,
  };
};

interface UseReviewsParams {
  isOpen: boolean;
  locationName?: string;
  accountName?: string;
  subjectEmail?: string;
  max?: number;
}

export const useReviews = ({ isOpen, locationName, accountName, subjectEmail, max }: UseReviewsParams) => {
  const query = useQuery({
    queryKey: ['reviews', locationName, accountName, subjectEmail],
    queryFn: () => fetchReviews({ locationName: locationName!, accountName, subjectEmail, max }),
    enabled: isOpen && !!locationName,  
    staleTime: 1000 * 60 * 5,
  });

  return {
    reviewsData: query.data,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
};